// The cart as one store per page load, shared by every Shop block on
// the page and the cart panel. Kept in localStorage per SITE (a vanity
// domain and /site/{org} are different sites to a browser anyway), so
// it survives moving between pages and a reload. Storage can be
// missing or refuse a write — a private window, a full quota — and
// then the cart simply lasts as long as the page, which still works.
import { computed, ref, watch } from 'vue';
import { addLine, cartCount, cartQuery, cartShipping, cartTotal, normaliseCart, setQty } from './shopCart.js';
import { checkoutReturn, sitePrefix } from './useSiteForm.js';

const lines = ref([]);
const panelOpen = ref(false);
let loaded = false;

function storageKey() {
    return `tiq-cart:${window.location.host}${sitePrefix()}`;
}

function load() {
    if (loaded || typeof window === 'undefined') return;
    loaded = true;
    try {
        lines.value = normaliseCart(JSON.parse(window.localStorage.getItem(storageKey()) || '[]'));
    } catch (e) {
        lines.value = [];
    }
    // Back from a paid checkout: the order is placed, so the cart that
    // made it is done.
    if (checkoutReturn().paid === 'shop') lines.value = [];
    watch(lines, (v) => {
        try { window.localStorage.setItem(storageKey(), JSON.stringify(v)); } catch (e) { /* page-lifetime cart */ }
    }, { deep: true, immediate: true });
    // Another tab of the same site changed it.
    window.addEventListener('storage', (e) => {
        if (e.key !== storageKey()) return;
        try { lines.value = normaliseCart(JSON.parse(e.newValue || '[]')); } catch (err) { /* keep ours */ }
    });
}

// Which Shop block draws the cart panel: the first to ask. A token
// rather than a count, so a block that unmounts hands it back and the
// next block to mount can take it.
let cartOwner = null;
export function claimCart(token) {
    if (cartOwner === null) cartOwner = token;
    return cartOwner === token;
}
export function releaseCart(token) {
    if (cartOwner === token) cartOwner = null;
}

export function useShopCart() {
    load();
    return {
        lines,
        panelOpen,
        count: computed(() => cartCount(lines.value)),
        total: computed(() => cartTotal(lines.value)),
        shipping: computed(() => cartShipping(lines.value)),
        add(product, qty = 1) {
            const before = lines.value;
            lines.value = addLine(before, product, qty);
            return lines.value !== before;
        },
        setQty(index, qty) { lines.value = setQty(lines.value, index, qty); },
        remove(index) { lines.value = setQty(lines.value, index, 0); },
        checkoutUrl() {
            return `${sitePrefix()}/shop/cart?${cartQuery(lines.value, window.location.pathname)}`;
        },
    };
}
